import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../ui/dialog";
import {
  Gift, Plus, Trash2, Copy, Check, Search,
  Ticket, Users, Clock, Ban
} from "lucide-react";

export const GiftCodes = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, available: 0, inactive: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [formData, setFormData] = useState({ code: "", code_type: "12_months", note: "", count: 5 });
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState("");
  const { token } = useAuth();

  const fetchCodes = async () => {
    try {
      const [codesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/gift-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/gift-codes/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCodes(codesRes.data);
      setStats(statsRes.data);
    } catch { toast.error("Erreur de chargement"); }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async () => {
    try {
      if (bulkMode) {
        const res = await axios.post(`${API}/admin/gift-codes/bulk`, {
          count: formData.count,
          code_type: formData.code_type,
          note: formData.note
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`${res.data.created} codes créés`);
      } else {
        await axios.post(`${API}/admin/gift-codes`, {
          code: formData.code || null,
          code_type: formData.code_type,
          note: formData.note
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Code créé");
      }
      setDialogOpen(false);
      setFormData({ code: "", code_type: "12_months", note: "", count: 5 });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/admin/gift-codes/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCodes();
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce code ?")) return;
    try {
      await axios.delete(`${API}/admin/gift-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Code supprimé");
      fetchCodes();
    } catch { toast.error("Erreur"); }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    toast.success("Code copié");
    setTimeout(() => setCopied(""), 2000);
  };

  const filteredCodes = codes.filter(c => {
    if (filter === "available" && (c.is_used || !c.is_active)) return false;
    if (filter === "used" && !c.is_used) return false;
    if (filter === "inactive" && c.is_active) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return c.code.toLowerCase().includes(q) ||
             (c.used_by_login || "").toLowerCase().includes(q) ||
             (c.note || "").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="gift-codes-page">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total", value: stats.total, icon: Ticket, color: "text-primary" },
          { label: "Disponibles", value: stats.available, icon: Gift, color: "text-green-500" },
          { label: "Utilisés", value: stats.used, icon: Users, color: "text-accent" },
          { label: "Désactivés", value: stats.inactive, icon: Ban, color: "text-destructive" },
        ].map(s => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un code, utilisateur ou note..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-codes"
          />
        </div>
        <div className="flex gap-1">
          {[
            { key: "all", label: "Tous" },
            { key: "available", label: "Disponibles" },
            { key: "used", label: "Utilisés" },
            { key: "inactive", label: "Désactivés" },
          ].map(f => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <Button onClick={() => { setDialogOpen(true); setBulkMode(false); }} data-testid="create-code-btn">
          <Plus className="h-4 w-4 mr-1" /> Créer
        </Button>
        <Button variant="outline" onClick={() => { setDialogOpen(true); setBulkMode(true); }} data-testid="bulk-create-btn">
          <Plus className="h-4 w-4 mr-1" /> Créer en lot
        </Button>
      </div>

      {/* Codes table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium">Code</th>
                  <th className="text-left p-3 font-medium">Type</th>
                  <th className="text-left p-3 font-medium">Statut</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Utilisé par</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Note</th>
                  <th className="text-left p-3 font-medium hidden lg:table-cell">Date</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map(code => (
                  <tr key={code.id} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-primary font-semibold">{code.code}</code>
                        <button
                          onClick={() => handleCopy(code.code)}
                          className="text-muted-foreground hover:text-primary"
                        >
                          {copied === code.code ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={code.code_type === "lifetime" ? "default" : "secondary"}>
                        {code.code_type === "lifetime" ? "Lifetime" : "12 mois"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {code.is_used ? (
                        <Badge variant="outline" className="text-muted-foreground">Utilisé</Badge>
                      ) : code.is_active ? (
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Disponible</Badge>
                      ) : (
                        <Badge variant="destructive">Désactivé</Badge>
                      )}
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      {code.used_by_login ? (
                        <span className="text-accent">{code.used_by_login}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 hidden md:table-cell text-muted-foreground text-xs">
                      {code.note || "—"}
                    </td>
                    <td className="p-3 hidden lg:table-cell text-muted-foreground text-xs">
                      {code.created_at ? new Date(code.created_at).toLocaleDateString("fr-FR") : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Switch
                          checked={code.is_active}
                          onCheckedChange={() => handleToggle(code.id)}
                          disabled={code.is_used}
                          data-testid={`toggle-code-${code.id}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(code.id)}
                          data-testid={`delete-code-${code.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCodes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      Aucun code trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="create-code-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              {bulkMode ? "Créer des codes en lot" : "Créer un code gift"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!bulkMode && (
              <div className="space-y-2">
                <Label>Code (optionnel, auto-généré si vide)</Label>
                <Input
                  placeholder="Ex: ABCD-1234-EFGH"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  data-testid="code-input"
                />
              </div>
            )}
            {bulkMode && (
              <div className="space-y-2">
                <Label>Nombre de codes (max 50)</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={formData.count}
                  onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) || 1 })}
                  data-testid="count-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="flex gap-2">
                <Button
                  variant={formData.code_type === "12_months" ? "default" : "outline"}
                  onClick={() => setFormData({ ...formData, code_type: "12_months" })}
                  className="flex-1"
                  data-testid="type-12months"
                >
                  <Clock className="h-4 w-4 mr-1" /> 12 mois
                </Button>
                <Button
                  variant={formData.code_type === "lifetime" ? "default" : "outline"}
                  onClick={() => setFormData({ ...formData, code_type: "lifetime" })}
                  className="flex-1"
                  data-testid="type-lifetime"
                >
                  <Gift className="h-4 w-4 mr-1" /> Lifetime
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note (optionnel)</Label>
              <Input
                placeholder="Ex: Promo été 2026"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                data-testid="note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} data-testid="confirm-create">
              {bulkMode ? `Créer ${formData.count} codes` : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
